import { useState, useEffect, forwardRef, useImperativeHandle, useRef, useCallback, useMemo } from "react";
import PropTypes from 'prop-types';

//Components
import { Grid, IconButton, CircularProgress, Chip, Skeleton } from "@mui/material";

import { Tooltip, InputTags, ToggleSwitch } from "@gpstrackit/react-controls";
import Table from './VirtualTable';
import MultiLevelSelector from 'Components/SelectorMultiLevel'
import { TableButtons } from '../TableButtons'
import SearchInput from './SearchInput';
import moment from 'moment';
//Preferences
import Preferences from 'Core/containers/Preferences';
import { getPMUser } from 'Core/data/Helpers';
//Language
import {
    useIntl, 
} from "react-intl";

//Icons
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterIcon from '@mui/icons-material/FilterList';
import DensityIcon from '@mui/icons-material/LineWeight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
//Styles
import './dataView.scss';


import NotificationManager from 'Components/Notifications/NotificationManager';

import {
    useSelector,
    useDispatch,
} from "react-redux";

//Actions
import {
    genericExportSend,
    clearRedux,
} from "Redux/actions";


import {
    fetchInfiniteScrollData,
    fetchInfiniteScrollTotalCount,
    initEntity,
    clearEntity,
    resetEntityTotalCount,
    selectEntityState,
    abortEntityRequest,
} from 'Core/common/redux/infiniteScrollSlice';
//Helpers
import { validateEpochDate } from 'Core/data/Helpers';
import { has, isEqual } from "lodash";

// Shared GraphQL query builder for data and total queries
/**
 * Build a GraphQL query string for data fetching operations.
 * 
 * @param {Object} config - Query configuration
 * @param {string} config.functionName - GraphQL function name (e.g., 'searchUnits')
 * @param {string} config.conditionsType - GraphQL conditions input type (e.g., 'UnitSearchInput')
 * @param {string} config.sortType - GraphQL sort input type (e.g., 'UnitSortInput')
 * @param {string} config.fields - Fields to query in items (e.g., 'id name esn')
 * @param {string} [config.extraFields=''] - Additional result fields ('nextToken' | 'total')
 * @returns {string} GraphQL query string
 */
const buildGraphQLQuery = ({
    functionName,
    conditionsType,
    sortType,
    fields,
    extraFields = '',
}) => {
    const sortParam = sortType ? `, $sort: [${sortType}]` : '';
    const sortArg = sortType ? ', sort: $sort' : '';
    return `
        query ${functionName} ($conditions: [${conditionsType}]${sortParam}, $limit: Int, $offset: Int) {
            result: ${functionName} (conditions: $conditions${sortArg}, limit: $limit, offset: $offset) {
                items {
                    ${fields}
                }
                ${extraFields}
            }
        }
    `;
};

// Module-level style constants for stable references
const ICON_STYLE = { color: 'black', fontSize: 20 };
const FILTER_BUTTON_STYLE = { padding: 0, marginRight: 15, color: 'black' };
const TOOLBAR_BUTTON_STYLE = { padding: 0, marginRight: 15 };
const DENSITY_BUTTON_STYLE = { padding: 0, marginRight: 10 };
const JUSTIFY_END_STYLE = { justifyContent: 'end' };
const TABLE_WRAPPER_STYLE = { marginTop: 10 };
const TOTAL_BADGE_WRAPPER_STYLE = { marginLeft: 12, display: 'flex', alignItems: 'center' };

// Named constants for default values
const DEFAULT_ROWS = 50;
const DEFAULT_DENSITY = 3;
const SEARCH_DEBOUNCE_MS = 500;
const EXPORT_LIMIT = 10;

// Builds dynamic GraphQL fields from visible columns
const buildDynamicFields = (columns, baseQuery, includeHidden = false) => {
    let params = baseQuery;
    const columnsToUse = includeHidden
        ? columns.filter((item) => item.export)
        : columns.filter((item) => item.show);

    columnsToUse.map((column) => {
        params += " "
        let fields = column.field && column.field.split(",") || []
        fields.map(f => {
            let array = f.split(".")
            if (array.length > 1) {
                let text = "";
                let closeText = "";
                if (params.includes(array[0])) {
                    let divider = params.split(`${array[0]} { `)
                    params = divider[0] + ` ${array[0]} { ` + `${array[1]} ` + divider[1]
                } else {
                    for (let i = 0; i < array.length; i++) {
                        if (!params.includes(array[0])) {
                            text += array[i]
                        }
                        if (i < array.length - 1) {
                            text += " { "
                            closeText += " } "
                        }
                    }
                }
                params += text + closeText
            }
            else params += array[0]
        })
    })
    return params;
};

// Formats export values (date/time) for CSV export
const formatExportValue = (val, format, timezone) => {
    let formatted = "";
    switch (format) {
        case "date":
            formatted = moment(val).format("MMM DD YYYY")
            break;
        case "datetime":
            formatted = moment(val).format("MMM DD YYYY h:mm:ss a")
            break;
        case "time":
            formatted = moment(val).format("h:mm:ss a")
            break;
        case "timezone":
            formatted = moment(val).tz(timezone).format("YYYY-MM-DD HH:mm:ss z")
            break;
        default:
            formatted = val; // if there is no format return the same value
            break;
    }
    return formatted;
};

// Builds timestamped export file name
const buildExportFileName = (name, ext) => {
    let date = new Date();
    return `${name}_${date.getFullYear()}${date.getMonth()}${date.getDay()}${date.getHours()}${date.getMinutes()}.${ext}`;
};

/**
 * Deduplicate GraphQL conditions by deep comparison.
 * 
 * WHY: When permanentConditions and onChangeConditions both push the same
 * mandatory filter (e.g., companyId), the merged apiConditions array contains
 * duplicates. The backend handles this harmlessly (AND with same values), but
 * it bloats the GraphQL payload and makes debugging harder.
 * 
 * HOW: Compares each condition's meaningful properties (fields, terms, exact,
 * conditionOperator) using lodash isEqual for deep array/value comparison.
 * Keeps the FIRST occurrence of each unique condition (permanentConditions
 * are appended second, so user conditions take precedence).
 * 
 * @param {Array<Object>} conditions - Merged conditions array (sanitized, no tags)
 * @returns {Array<Object>} Deduplicated conditions array
 */
const deduplicateConditions = (conditions) => {
    if (!conditions?.length) return conditions;

    const seen = [];
    return conditions.filter((condition) => {
        // Compare only the properties that affect the GraphQL query.
        // 'tags' is already stripped by sanitization before this point.
        const signature = {
            fields: condition.fields,
            terms: condition.terms,
            exact: condition.exact,
            conditionOperator: condition.conditionOperator,
        };

        // Check if we've already seen an identical condition
        const isDuplicate = seen.some((s) => isEqual(s, signature));
        if (!isDuplicate) {
            seen.push(signature);
        }
        return !isDuplicate;
    });
};

const DataView = forwardRef((props, ref) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const tableRef = useRef();
    const preferencesRef = useRef();
    const {
        // ═══════════════════════════════════════════════════════════════════════════
        // CORE PROPS (remain flat - fundamental component configuration)
        // ═══════════════════════════════════════════════════════════════════════════
        columns,
        keyColumn = "id",
        entity = "default-list",
        name,
        infiniteScroll = true,  // Default: infinite scroll enabled; set false to use traditional pagination
        options,
        tableButtons,
        menuOptions,
        onClickMenu,
        customColumn,
        extraButton,
        extraFilters,
        showError,
        clearOnUnmount = false,  // Clear entity state when component unmounts (Common Redux mode)
        
        // ═══════════════════════════════════════════════════════════════════════════
        // EVENT CALLBACKS (remain flat - standard React callback pattern)
        // ═══════════════════════════════════════════════════════════════════════════
        onChangePage,
        onChangeRowsPerPage,
        onChangeSearch,
        onChangeConditions,     // Called when conditions change (search, filters)
        onChangeColumns,        // Called when columns change (reorder, resize, visibility)
        onSelectedItems,        // Called when selection changes
        onFilters,              // Called when filter button is clicked
        onExternalSorting,      // Called for external sort handling
        onChangeTotal,          // Called when total count changes (non-infinite scroll)
        
        // ═══════════════════════════════════════════════════════════════════════════
        // GROUPED PROPS (recommended - organized by concern)
        // ═══════════════════════════════════════════════════════════════════════════
        graphqlConfig,
        externalDataConfig,
        searchConfig,
        exportConfig,
        uiConfig,
        selectionConfig,
        preferencesConfig,
        defaultsConfig,
        messagesConfig,
        emptyConfig,      // Empty state configuration { type, description, image, actionText, onAction }
        virtualizationConfig,
        
        // ═══════════════════════════════════════════════════════════════════════════
        // TOTAL COUNT CONFIGURATION
        // ═══════════════════════════════════════════════════════════════════════════
        showTotalCount = true,  // Whether to show total count badge and fetch total count
        
        // ═══════════════════════════════════════════════════════════════════════════
        // PERMANENT CONDITIONS
        // ═══════════════════════════════════════════════════════════════════════════
        // FIX: Conditions that are ALWAYS merged into every getData() call (initial, refresh,
        // search, sort, scroll). Prevents mandatory filters (e.g., companyId) from being
        // dropped when onChangeConditions hasn't fired yet (no-preferences scenario) or
        // when refresh/sort bypass the parent's condition transformation.
        // See: BUG-ANALYSIS-no-preferences-mandatory-conditions.md (Solution B)
        permanentConditions,
        
    } = props;

    // ════════════════════════════════════════════════════════════════════════════
    // PROPS DESTRUCTURING WITH DEFAULTS
    // ════════════════════════════════════════════════════════════════════════════
    
    /**
     * @typedef {Object} GraphQLConfig
     * @property {string} [function] - GraphQL function name
     * @property {string} [sortType] - Sort input type
     * @property {string} [conditionsType] - Conditions input type  
     * @property {string} [customQuery] - Custom fields to query
     * @property {boolean} [dynamicQuery] - Build query from visible columns
     */
    const {
        function: graphqlFunction,
        sortType: graphqlSortType,
        conditionsType: graphqlConditionsType,
        customQuery: graphqlCustomQuery,
        dynamicQuery: graphqlDynamicQuery = false,
        totalBaseField: graphqlTotalBaseField = 'id',
    } = graphqlConfig ?? {};

    /**
     * @typedef {Object} ExternalDataConfig
     * @property {Array} [data] - Data items
     * @property {boolean} [loading] - Loading state
     * @property {string} [nextToken] - Next token for pagination
     * @property {number} [total] - Total count (for non-infinite scroll)
     * @property {number} [totalCount] - Total count (for infinite scroll badge)
     * @property {boolean} [totalCountLoading] - Total count loading state
     * @property {string|null} [totalCountError] - Total count error state (null = no error)
     * @property {Function} [onFetchRequest] - Callback for data fetch
     * @property {Function} [onFetchTotalRequest] - Callback for total count fetch
     * @property {Function} [onRefresh] - Callback for refresh
     * @property {string} [error] - Error message
     */
    const {
        data: externalData,
        loading: externalLoading,
        nextToken: externalNextToken,
        total: externalTotal,
        totalCount: externalTotalCount,
        totalCountLoading: externalTotalCountLoading,
        totalCountError: externalTotalCountError = null,
        onFetchRequest: externalOnFetchRequest,
        onFetchTotalRequest: externalOnFetchTotalRequest,
        onRefresh: externalOnRefresh,
        error: externalError = null,
    } = externalDataConfig ?? {};

    /**
     * @typedef {Object} SearchConfig
     * @property {boolean} [disabled] - Disable search
     * @property {string} [placeholder] - Placeholder text
     * @property {number} [barWidth] - Width of search bar
     * @property {Object} [conditions] - Initial conditions
     * @property {string} [optionValue] - Value field for options
     * @property {string} [optionLabel] - Label field for options
     * @property {boolean} [custom] - Custom search component
     * @property {string} [extraField] - Extra field for search
     * @property {boolean} [noAutomatic] - Disable automatic search
     * @property {boolean} [noOptions] - Disable options
     * @property {Array} [fields] - Fields to search
     * @property {boolean} [openCustomOptions] - Open custom options
     * @property {boolean} [onlyEnter] - Search only on enter
     * @property {boolean} [simple] - Simple search mode
     * @property {boolean} [noTags] - Disable tags
     * @property {Object} [style] - Custom style
     * @property {boolean} [fixBar] - Fix search bar
     * @property {number} [tagsLimit] - Maximum number of tags allowed in search (default: 250)
     */
    const {
        disabled: searchDisabled = false,
        placeholder: searchPlaceholder,
        barWidth: searchBarWidth = 70,
        conditions: searchConditions,
        optionValue: searchOptionValue,
        optionLabel: searchOptionLabel,
        custom: searchCustom,
        extraField: searchExtraField,
        noAutomatic: searchNoAutomatic = false,
        noOptions: searchNoOptions = false,
        fields: searchFields,
        openCustomOptions: searchOpenCustomOptions = false,
        onlyEnter: searchOnlyEnter = false,
        clearGridOnEmpty: searchClearGridOnEmpty = false,
        simple: searchSimple = false,
        noTags: searchNoTags = false,
        style: searchStyle,
        fixBar: searchFixBar = false,
        tagsLimit: searchTagsLimit = 250,
    } = searchConfig ?? {};

    /**
     * @typedef {Object} ExportConfig
     * @property {string} [fileName] - File name
     * @property {number} [limit] - Export limit
     * @property {boolean} [useDataGrid] - Use DataGrid for export
     * @property {Function} [generate] - Custom generation function
     * @property {boolean} [isPXSource] - Is PX source
     * @property {string} [pxAccount] - PX account
     * @property {boolean} [isDynamo] - Is Dynamo source
     */
    const {
        fileName: exportFileName,
        limit: exportLimit = EXPORT_LIMIT,
        useDataGrid: exportUseDataGrid = false,
        generate: exportGenerate,
        isPXSource: exportIsPXSource = false,
        pxAccount: exportPxAccount,
        isDynamo: exportIsDynamo = false,
    } = exportConfig ?? {};

    /**
     * @typedef {Object} UIConfig
     * @property {number} [topHeight] - Top height
     * @property {string|number} [height] - Height
     * @property {boolean} [hideFilters] - Hide filters
     * @property {boolean} [hideRefresh] - Hide refresh
     * @property {boolean} [hideDensity] - Hide density
     * @property {boolean} [hidePagination] - Hide pagination
     * @property {boolean} [hideSplit] - Hide split
     * @property {boolean} [toolbarFullWidth] - Toolbar full width
     * @property {boolean} [flexEnd] - Flex end
     * @property {boolean} [filterSelected] - Filter selected
     * @property {boolean} [splitNoDevices] - Split no devices
     * @property {boolean} [buttonsFlexWrap] - Table top-level buttons with flex wrap (default: true)
     */
    const {
        topHeight: uiTopHeight,
        height: uiHeight,
        hideFilters: uiHideFilters = false,
        hideRefresh: uiHideRefresh = false,
        hideDensity: uiHideDensity = false,
        hidePagination: uiHidePagination = false,
        hideAllInPagination: uiHideAllInPagination = false,
        hideSplit: uiHideSplit = false,
        toolbarFullWidth: uiToolbarFullWidth = false,
        flexEnd: uiFlexEnd = false,
        filterSelected: uiFilterSelected = false,
        splitNoDevices: uiSplitNoDevices = false,
        buttonsFlexWrap: uiButtonsFlexWrap = true,
        optionsButtonColSum: uiOptionsButtonColSum = 2 
    } = uiConfig ?? {};

    // Default callback shows notification when selection limit is reached
    const defaultOnLimitReached = (maxItems, attemptedCount, excess) => {
        const message = (messages?.['common_dataview_selection_limit_reached'] || 'Maximum selection limit reached ({max} items)')
            .replace('{max}', maxItems);
        const title = messages?.['common_dataview_selection_limit_title'] || 'Selection Limit';
        NotificationManager.warning(message, title, 3000);
    };

    /**
     * @typedef {Object} SelectionConfig
     * @property {boolean} [disabled] - Disable selection
     * @property {number} [maxItems] - Max selectable items
     * @property {boolean} [showCounter] - Show counter
     * @property {Function} [onLimitReached] - Limit reached callback
     */
    const {
        disabled: selectionDisabled = false,
        maxItems: selectionMaxItems = 500,
        showCounter: selectionShowCounter = true,
        onLimitReached: selectionOnLimitReached = defaultOnLimitReached,
    } = selectionConfig ?? {};

    /**
     * @typedef {Object} PreferencesConfig
     * @property {boolean} [disabled] - Disable preferences
     * @property {boolean} [noTags] - Disable tags
     * @property {boolean} [noSplit] - Don't read/persist the split preference (always starts unchecked)
     */
    const {
        disabled: preferencesDisabled = false,
        noTags: preferencesNoTags = false,
        noSplit: preferencesNoSplit = false,
    } = preferencesConfig ?? {};

    /**
     * @typedef {Object} DefaultsConfig
     * @property {Object} [sort] - Default sort (Query format: { field, order: "ASC"|"DESC" })
     * @property {number} [rowsPerPage] - Default rows per page
     * @property {Array} [valuesPerPage] - Default values per page
     */
    // NOTE: defaultsSort uses QUERY FORMAT { field, order: "ASC" } by design.
    // This matches the GraphQL API expectation. When used internally (e.g., updateSort),
    // normalizeSort() converts it to UI format { key, order: "asc" } for component state.
    const {
        sort: defaultsSort = { field: "id", order: "ASC" },
        rowsPerPage: defaultsRowsPerPage = DEFAULT_ROWS,
        valuesPerPage: defaultsValuesPerPage,
    } = defaultsConfig ?? {};

    /**
     * @typedef {Object} MessagesConfig
     * @property {string} [noMoreItems] - No more items message
     * @property {string} [emptyInitial] - Empty initial message
     * @property {string} [emptyNoData] - Empty no data message
     * @property {string} [emptyFiltered] - Empty filtered message
     * @property {string} [emptySearch] - Empty search message
     * @property {string} [emptyError] - Empty error message
     * @property {string} [selectionLimitReached] - Selection limit reached message
     * @property {string} [selectionLimitTitle] - Selection limit title
     * @property {string} [selectionLimitExceeded] - Selection limit exceeded message
     * @property {string} [selectionLimitTooltip] - Selection limit tooltip
     * @property {string} [dragToReorder] - Drag to reorder tooltip
     * @property {string} [totalLabel] - Total label
     */
    const {
        noMoreItems: messagesNoMoreItems = messages?.['no_more_items'],
        emptyInitial: messagesEmptyInitial = messages?.['common_dataview_empty_initial'],
        emptyNoData: messagesEmptyNoData = messages?.['common_dataview_empty_no_data'],
        emptyFiltered: messagesEmptyFiltered = messages?.['common_dataview_empty_filtered'],
        emptySearch: messagesEmptySearch = messages?.['common_dataview_empty_search'],
        emptyError: messagesEmptyError = messages?.['common_dataview_empty_error'],
        selectionLimitReached: messagesSelectionLimitReached = messages?.['common_dataview_selection_limit_reached'],
        selectionLimitTitle: messagesSelectionLimitTitle = messages?.['common_dataview_selection_limit_title'],
        selectionLimitExceeded: messagesSelectionLimitExceeded = messages?.['common_dataview_selection_limit_exceeded'],
        selectionLimitTooltip: messagesSelectionLimitTooltip = messages?.['common_dataview_selection_limit_tooltip'],
        dragToReorder: messagesDragToReorder = messages?.['common_dataview_drag_to_reorder'],
        totalLabel: messagesTotalLabel = messages?.['common_dataview_total_label'],
    } = messagesConfig ?? {};

    /**
     * @typedef {Object} VirtualizationConfig
     * @property {number} [skeletonCount] - Number of skeleton rows during load-more (default: 5)
     * @property {number} [rowHeight] - Default estimated row height in pixels (default: 53)
     * @property {number} [baseRowHeight] - Base row height for density calculations (default: 21)
     * @property {number} [condensationHeight] - Row height adjustment per density level (default: 7)
     * @property {number} [heightMultiplier] - Multiplier for density height calculation (default: 2)
     * @property {number} [overscan] - Buffer rows above/below viewport for smooth scrolling (default: 15)
     * @property {number} [scrollThreshold] - Rows from end to trigger load-more (default: 5)
     * @property {number} [lockedColumnOffset] - Base pixel offset for locked columns (default: 43)
     */
    // Note: virtualizationConfig is passed through directly to VirtualTable without destructuring
    // See VirtualTable/index.js for default values and usage
    // ════════════════════════════════════════════════════════════════════════════
    // MODE DETECTION (Common Redux vs External Data)
    // ════════════════════════════════════════════════════════════════════════════
    
    /**
     * Determines data mode: Common Redux (entity without externalDataConfig) vs External Data.
     * Mode is determined by prop presence, not a boolean flag.
     */
    const useCommonRedux = useMemo(() => {
        const hasValidEntity = entity && entity !== 'default-list';
        const hasExternalData = !!externalDataConfig || !!externalOnFetchRequest;
        
        // Common Redux mode: entity provided WITHOUT external data
        return hasValidEntity && !hasExternalData;
    }, [entity, externalDataConfig, externalOnFetchRequest]);

    // Dev mode validation warnings for mode configuration
    useEffect(() => {
        if (process.env.NODE_ENV !== 'development') return;
        
        const hasValidEntity = entity && entity !== 'default-list';
        const hasExternalData = !!externalDataConfig;
        
        // Error when neither mode is configured
        if (!hasValidEntity && !hasExternalData) {
            console.error(
                '[DataViewInfiniteScroll] Invalid configuration: ' +
                'Either provide "entity" prop (for common Redux mode) or ' +
                '"externalDataConfig" (for external data mode). ' +
                'Component will not fetch or display data correctly.'
            );
        }
        
        // Warning when both modes are configured (externalData takes precedence)
        if (hasValidEntity && hasExternalData) {
            console.warn(
                '[DataViewInfiniteScroll] Both "entity" and "externalDataConfig" provided. ' +
                'externalDataConfig takes precedence for data fetching. ' +
                '"entity" will only be used for preferences namespacing. ' +
                'Recommendation: Choose one approach for clarity.'
            );
        }
        
        // Warning when infinite scroll enabled but no fetch handler
        if (hasExternalData && infiniteScroll) {
            if (!externalDataConfig?.onFetchRequest) {
                console.warn(
                    '[DataViewInfiniteScroll] infiniteScroll enabled with externalDataConfig ' +
                    'but no onFetchRequest handler provided. Infinite scroll will not load more data.'
                );
            }
        }
    }, []); // Run once on mount

    //Redux States
    const data = useSelector(state => state.commonRedux[entity]);
    const errorDataView = useSelector(state => state.commonRedux.errorDataView);
    const loadingPreference = useSelector(state => state.preferencesRedux?.loadingPreference);
    const loading = useSelector(state => state.commonRedux[`loading-${entity}`]);
    const { change } = useSelector(state => state.commonRedux);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);

    // Common Redux selectors - only active when useCommonRedux is true
    // Uses selectEntityState from commonISRedux slice for entity-namespaced state
    const commonReduxEntityState = useSelector(
        useCommonRedux ? selectEntityState(entity) : () => null
    );

    // Resolved data sources - pick from common Redux or external data based on mode
    // These memos provide a unified interface for the rest of the component
    
    /**
     * Resolved data items from Common Redux or External mode
     */
    const resolvedData = useMemo(() => {
        if (useCommonRedux) {
            return commonReduxEntityState?.items ?? [];
        }
        return externalData ?? [];
    }, [useCommonRedux, commonReduxEntityState?.items, externalData]);

    /**
     * RQ-05b: Resolved loading state
     * - Common Redux mode: loading from commonISRedux entity state
     * - External mode: loading from externalDataConfig
     */
    const resolvedLoading = useMemo(() => {
        if (useCommonRedux) {
            return commonReduxEntityState?.loading ?? false;
        }
        return externalLoading ?? false;
    }, [useCommonRedux, commonReduxEntityState?.loading, externalLoading]);

    /**
     * RQ-05c: Resolved nextToken for infinite scroll pagination
     * - Common Redux mode: nextToken from commonISRedux entity state
     * - External mode: nextToken from externalDataConfig
     */
    const resolvedNextToken = useMemo(() => {
        if (useCommonRedux) {
            return commonReduxEntityState?.nextToken ?? null;
        }
        return externalNextToken ?? null;
    }, [useCommonRedux, commonReduxEntityState?.nextToken, externalNextToken]);

    /**
     * RQ-05d: Resolved total count for infinite scroll badge
     * - Common Redux mode: totalCount from commonISRedux entity state
     * - External mode: totalCount from externalDataConfig
     */
    const resolvedTotalCount = useMemo(() => {
        if (useCommonRedux) {
            return commonReduxEntityState?.totalCount ?? null;
        }
        return externalTotalCount ?? null;
    }, [useCommonRedux, commonReduxEntityState?.totalCount, externalTotalCount]);

    /**
     * RQ-05e: Resolved total count loading state
     * - Common Redux mode: totalCountLoading from commonISRedux entity state
     * - External mode: totalCountLoading from externalDataConfig
     */
    const resolvedTotalCountLoading = useMemo(() => {
        if (useCommonRedux) {
            return commonReduxEntityState?.totalCountLoading ?? false;
        }
        return externalTotalCountLoading ?? false;
    }, [useCommonRedux, commonReduxEntityState?.totalCountLoading, externalTotalCountLoading]);

    /**
     * RQ-TOTAL-ERR-01: Resolved total count error state
     * - Common Redux mode: totalCountError from commonISRedux entity state
     * - External mode: totalCountError from externalDataConfig
     * Used to display retry UI on total badge when total query fails
     */
    const resolvedTotalCountError = useMemo(() => {
        if (useCommonRedux) {
            return commonReduxEntityState?.totalCountError ?? null;
        }
        return externalTotalCountError ?? null;
    }, [useCommonRedux, commonReduxEntityState?.totalCountError, externalTotalCountError]);

    /**
     * BUG-FIX: Resolved error state for empty state error type rendering
     * - Common Redux mode: error from commonISRedux entity state
     * - External mode: error from externalDataConfig
     */
    const resolvedError = useMemo(() => {
        if (useCommonRedux) {
            return commonReduxEntityState?.error ?? null;
        }
        return externalError ?? null;
    }, [useCommonRedux, commonReduxEntityState?.error, externalError]);

    const resolvedShowFirstFilters = useMemo(() => {
        return !uiHideSplit || extraFilters || !searchDisabled || searchSimple
    }, [uiHideSplit, extraFilters, searchDisabled, searchSimple]);

    const resolvedTopTableGridWidths = useMemo(() => {
        let baseWidth = 3;
        const hasOptions = options?.length > 0
        
        if(showTotalCount) baseWidth++;
        if(hasOptions) baseWidth+=uiOptionsButtonColSum || 2;

        return {
            tableButtons: baseWidth,
            restFilters: 12 - baseWidth,
            hasOptions,
        };
    }, [showTotalCount, options?.length, uiOptionsButtonColSum]);

    //Local States
    // ══════════════════════════════════════════════════════════════════════════════
    // SORT FORMAT DOCUMENTATION
    // ══════════════════════════════════════════════════════════════════════════════
    // This component uses TWO sort formats for different layers:
    //
    // UI FORMAT (component state, preferences, VirtualTable):
    //   { key: "fieldName", order: "asc" | "desc" }
    //   - 'key' matches column.key in column definitions
    //   - lowercase order for MUI TableSortLabel compatibility
    //
    // QUERY FORMAT (GraphQL API, defaultsSort):
    //   { field: "fieldName", order: "ASC" | "DESC" }
    //   - 'field' for GraphQL variable naming convention
    //   - uppercase order for GraphQL enum values
    //
    // The 'sort' state below is stored in UI FORMAT after applySortPreferences/
    // updateSort normalize it. Always use normalizeSort() when converting between
    // formats. See normalizeSort() and updateSort() for the transformation logic.
    // ══════════════════════════════════════════════════════════════════════════════
    const [checked, setChecked] = useState(false);
    const [rows, setRows] = useState(DEFAULT_ROWS);
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState(defaultsSort);
    const [conditions, setConditions] = useState([]);
    const [density, setDensity] = useState(DEFAULT_DENSITY);
    const [selectedItems, setSelectedItems] = useState([]);
    const [tags, setTags] = useState([])
    const [valueSearch, setValueSearch] = useState("");
    const [graphql, setGraphql] = useState("");
    const [isLoadingMore, setIsLoadingMore] = useState(false); // Track infinite scroll loading state
    const [isTotalCountPending, setIsTotalCountPending] = useState(false); // Placeholder state for badge on mode switch
    // Ref to track previous conditions for detecting filter-only changes vs sort-only changes
    const prevConditionsRef = useRef(null);
    const prevSortRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const prevShowTotalCountRef = useRef(showTotalCount);
    const dataFetchedWhileHiddenRef = useRef(false); // Track if getData() was called while showTotalCount was false

    /**
     * Phase 6: Computed empty state type based on current filters/search state.
     * - Returns null if data exists or is loading
     * - Returns 'error' if there's an error from API (timeout, network error, etc.)
     * - Returns 'initial' if no API request fired yet (Phase 7)
     *   - Common Redux mode: lastConditions and lastSort are null
     *   - External mode: data is undefined/null (not empty array)
     * - Returns 'search-empty' if search/tags are active
     * - Returns 'filtered-empty' if filter conditions are active
     * - Returns 'no-data' as default when no data and no filters/search
     */
    const computedEmptyType = useMemo(() => {
        // Use resolved data to check if we have items
        const hasData = resolvedData?.length > 0;
        
        // BUG-FIX: Check for error state first - show error message when API fails
        // This handles timeout, network errors, and other API failures
        if (resolvedError && !hasData && !resolvedLoading) {
            return 'error';
        }
        
        if (hasData || resolvedLoading) return null;
        
        // This shows 'Select your filters to search your items' message
        if (useCommonRedux) {
            // Common Redux mode: lastConditions and lastSort are null until first fetch
            if (commonReduxEntityState) {
                const neverFetched = commonReduxEntityState.lastConditions === null && 
                                    commonReduxEntityState.lastSort === null &&
                                    !commonReduxEntityState.loading;
                if (neverFetched) return 'initial';
            }
        } else {
            // External data mode: data being undefined/null indicates never fetched
            // vs empty array [] which indicates fetched but no results
            // Parent components should initialize with undefined/null, then [] after first fetch
            const neverFetched = (externalData === undefined || externalData === null) && 
                                 !externalLoading;
            if (neverFetched) return 'initial';
        }
        
        // Check if filters/search are active
        const hasActiveFilters = conditions?.length > 0;
        const hasActiveSearch = valueSearch?.length > 0 || tags?.length > 0;
        
        if (hasActiveSearch) return 'search-empty';
        if (hasActiveFilters) return 'filtered-empty';
        return 'no-data';
    }, [resolvedData?.length, resolvedLoading, resolvedError, conditions, valueSearch, tags, useCommonRedux, commonReduxEntityState, externalData, externalLoading]);

    // Reset isLoadingMore when external loading finishes
    // Also reset when common Redux loading finishes
    useEffect(() => {
        const currentLoading = useCommonRedux ? resolvedLoading : externalLoading;
        if (!currentLoading && isLoadingMore) {
            setIsLoadingMore(false);
        }
    }, [useCommonRedux, resolvedLoading, externalLoading, isLoadingMore]);

    /**
     * Internal fetch handler for main data (common Redux mode).
     * Dispatches fetchInfiniteScrollData thunk to commonISRedux slice.
     * Only active when useCommonRedux is true.
     */
    const handleInternalFetch = useCallback((params) => {
        if (!useCommonRedux) return;
        
        const { query, variables, isLoadMore } = params;
        dispatch(fetchInfiniteScrollData({
            entity,
            query,
            variables,
            isLoadMore,
        }));
    }, [dispatch, entity, useCommonRedux]);

    /**
     * Internal total count handler (common Redux mode).
     * Dispatches fetchInfiniteScrollTotalCount thunk to commonISRedux slice.
     * Only active when useCommonRedux is true.
     */
    const handleInternalTotalFetch = useCallback((params) => {
        if (!useCommonRedux) return;
        
        const { query, conditions, sort, limit, offset } = params;
        dispatch(fetchInfiniteScrollTotalCount({
            entity,
            query,
            variables: { conditions, sort, limit, offset },
        }));
    }, [dispatch, entity, useCommonRedux]);

    /**
     * Resolved fetch handler - picks correct source based on mode.
     * - Common Redux mode: uses internal handler (dispatches to commonISRedux)
     * - External mode: uses external handler (calls parent callback)
     */ 
    const resolvedFetchHandler = useCallback((params) => {
        if (useCommonRedux) {
            handleInternalFetch(params);
        } else {
            const externalHandler = externalOnFetchRequest;
            externalHandler?.(params);
        }
    }, [useCommonRedux, handleInternalFetch, externalOnFetchRequest]);

    /**
     * Resolved total fetch handler - picks correct source based on mode.
     * - Common Redux mode: uses internal handler (dispatches to commonISRedux)
     * - External mode: uses external handler (calls parent callback)
     */
    const resolvedTotalFetchHandler = useCallback((params) => {
        if (useCommonRedux) {
            handleInternalTotalFetch(params);
        } else {
            const externalHandler = externalOnFetchTotalRequest;
            externalHandler?.(params);
        }
    }, [useCommonRedux, handleInternalTotalFetch, externalOnFetchTotalRequest]);

    /**
     * getData - Fetches data with the given parameters
     * @param {Object} options - Options object
     * @param {number} [options.newPage] - Page number to fetch
     * @param {number} [options.newRows] - Rows per page
     * @param {Object|Array} [options.newSort] - Sort configuration
     * @param {Array} [options.newConditions] - Filter conditions
     * @param {Array} [options.newColumns] - Column configuration
     * @param {Object} [options.filters] - Additional filters
     * @param {boolean} [options.showTotalCount] - Explicit override for total count fetch (bypasses stale prop closure)
     */
    const getData = (options = {}) => {
        const {
            newPage,
            newRows,
            newSort,
            newConditions,
            newColumns,
            filters,
            showTotalCount: showTotalCountOverride,
        } = options;

        let currentPage = page;
        let currentRows = rows;
        let currentSort = sort;
        let currentConditions = conditions;
        let currentColumns = columns;
        let params = graphqlCustomQuery || "id"
        // Only clear selection when NOT a load-more operation
        if (newColumns) currentColumns = newColumns

        // Build dynamic query fields using extracted pure function
        if (graphqlDynamicQuery) {
            params = buildDynamicFields(currentColumns, params);
        }

        // Build GraphQL query using shared template builder
        let query = buildGraphQLQuery({
            functionName: graphqlFunction,
            conditionsType: graphqlConditionsType,
            sortType: graphqlSortType,
            fields: params,
            extraFields: infiniteScroll ? 'nextToken' : 'total',
        });

        // ── Sort handling ──────────────────────────────────────────────
        // FIX: Use normalizeSort to ensure consistent format:
        // - Query format ({ field, order: "ASC" }) for GraphQL
        // - UI format ({ key, order: "asc" }) for state/VirtualTable
        if (newSort) {
            // newSort comes from updateSort (already query format) or direct call
            // Normalize to query format for GraphQL, but also update UI state
            const querySort = normalizeSort(newSort, 'query');
            const uiSort = normalizeSort(newSort, 'ui');
            currentSort = querySort || { field: 'id', order: 'ASC' };
            // Update state with UI format to keep arrow in sync
            setSort(uiSort);
            tableRef?.current?.setSort?.(uiSort);
        } else if (sort.key || sort.field) {
            // Use existing sort state - normalize it for query
            currentSort = normalizeSort(sort, 'query') || { field: 'id', order: 'ASC' };
            // No need to setSort here - state is already set correctly
        }

        // Sort field validation - if sorted column not visible, fallback to default sort
        // This handles the case where preferences contain a sort for a column that's now hidden
        let sortField = currentColumns?.find(item => item.field === currentSort.field || item.key === currentSort.field);
        if (currentColumns && currentColumns.length > 0 && !sortField) {
            console.warn("The current sort field is not present in the columns, falling back to default sort");
            // Use updateSort to sync ALL state layers (UI, tableRef, preferences) - Fix E
            // This ensures the arrow displays correctly AND preferences are persisted
            currentSort = updateSort(defaultsSort);
        }

        // ── Conditions handling ──────────────────────────────────────────
        if (newConditions) {
            currentConditions = newConditions;
            conditionsHandler(newConditions, newRows);
        }

        // ── Rows handling ────────────────────────────────────────────────
        if (newRows) {
            currentRows = newRows;
            setRows(newRows);
        }

        // ── Page handling (only mode-specific logic) ─────────────────────
        const isDataReset = !!(newSort || newConditions || newRows);

        if (isDataReset) {
            // Any data-changing operation resets to first page (both modes)
            currentPage = 0;
            resetToFirstPage();
        } else if (infiniteScroll) {
            // Infinite scroll: newPage > 0 = load more, newPage === 0 = explicit reset (refresh)
            if (newPage !== undefined && newPage > 0) {
                currentPage = newPage;
                setPage(newPage);
            } else if (newPage === 0) {
                currentPage = 0;
                resetToFirstPage();
            }
        } else {
            // Non-infinite: accept any explicit page (including 0)
            if (newPage || newPage === 0) {
                currentPage = newPage;
                setPage(newPage);
            }
        }

        setGraphql(query);

        if (filters && !filters.filterValidation) {
            return
        }

        // Unified fetch path using resolved handlers
        if (useCommonRedux || externalOnFetchRequest) {
            // Auto-calculate isLoadMore: true only when page > 0 in infinite scroll
            const isLoadMore = infiniteScroll && currentPage > 0;
            // Centralize isLoadingMore in getData
            setIsLoadingMore(isLoadMore);

            // Only clear selection when NOT a load-more operation
            if (!isLoadMore) {
                cleanSelectedItems();
                // Reset batch sequential processed tracking on new query
                tableRef?.current?.resetProcessed?.();
            }

            // Sanitize conditions: strip 'tags' before sending to GraphQL
            // 'tags' is used internally for preference persistence and UI state
            // 'conditionOperator' MUST be preserved - it controls AND vs OR logic in the backend
            // SearchBar conditions use 'OR' (match any field), AdvancedFilters use 'AND' (match all)
            const sanitizedConditions = currentConditions?.map(({ tags, account, ...condition }) => condition) || [];
            
            // FIX: Merge permanentConditions into every query to prevent mandatory filters
            // (e.g., companyId, accountId) from being dropped on refresh, sort, or scroll.
            // permanentConditions are never stripped or overridden — they're always present.
            // See: BUG-ANALYSIS-no-preferences-mandatory-conditions.md (Solution B)
            //
            // FIX: Deduplicate after merge to prevent redundant conditions in GraphQL payload.
            // When both permanentConditions and onChangeConditions push the same filter
            // (e.g., companyId), the merge creates duplicates. deduplicateConditions()
            // deep-compares fields/terms/exact/conditionOperator and keeps first occurrence.
            const mergedConditions = permanentConditions?.length
                ? [...sanitizedConditions, ...permanentConditions]
                : sanitizedConditions;
            const apiConditions = deduplicateConditions(mergedConditions);

            // Detect filter vs sort-only vs columns-only change for total count trigger
            // Use isEqual for deep comparison
            const conditionsChanged = !isEqual(currentConditions, prevConditionsRef.current);
            const sortChanged = !isEqual(currentSort, prevSortRef.current);
            const isSortOnlyChange = sortChanged && !conditionsChanged && !isInitialLoadRef.current;
            // Column visibility changes don't affect total count — skip re-fetch when only columns changed
            const isColumnsOnlyChange = newColumns && !conditionsChanged && !isInitialLoadRef.current;
            // Page/rows changes don't affect total count — skip re-fetch when only navigation changed
            const isPageOnlyChange = newPage !== undefined && !newSort && !newConditions && !newRows && !conditionsChanged && !isInitialLoadRef.current;
            const isRowsOnlyChange = !!newRows && !newConditions && !conditionsChanged && !isInitialLoadRef.current;

            // Auto-retry total count on next query if previous total failed
            const hasTotalCountError = !!resolvedTotalCountError;

            // Trigger total count fetch when: initial load, filters changed, or previous total failed
            // Skip when only sort, columns, page, or rows-per-page changed (total is unaffected)
            const shouldFetchTotal = (!isSortOnlyChange && !isColumnsOnlyChange && !isLoadMore && !isPageOnlyChange && !isRowsOnlyChange) || (hasTotalCountError && !isLoadMore && !isPageOnlyChange && !isRowsOnlyChange);
            // Update refs for next comparison
            prevConditionsRef.current = currentConditions;
            prevSortRef.current = currentSort;
            isInitialLoadRef.current = false;

            // Fire total count request if needed (via resolved handler)
            // Only fetch total count when showTotalCount is enabled
            // showTotalCountOverride bypasses stale closure when views call getData() synchronously from setType
            const effectiveShowTotalCount = showTotalCountOverride ?? showTotalCount;
            if (shouldFetchTotal && effectiveShowTotalCount) {
                setIsTotalCountPending(false); // Real fetch starting, clear placeholder
                // Build total query using shared template builder
                const totalQuery = buildGraphQLQuery({
                    functionName: graphqlFunction,
                    conditionsType: graphqlConditionsType,
                    fields: graphqlTotalBaseField,
                    extraFields: 'total',
                });
                resolvedTotalFetchHandler({
                    query: totalQuery,
                    conditions: apiConditions,
                    limit: 1,
                    offset: currentPage * currentRows,
                });
            }

            // Track that getData was called while total count badge is hidden (Last mode).
            // When user switches back to Range, this tells us the cached total is stale.
            if (!effectiveShowTotalCount) {
                dataFetchedWhileHiddenRef.current = true;
            }
            
            // Fire main data request (via resolved handler)
            resolvedFetchHandler({
                query,
                variables: {
                    limit: currentRows,
                    offset: currentPage * currentRows,
                    sort: currentSort,
                    conditions: apiConditions,
                },
                columns: currentColumns,
                isLoadMore
            });
            return; // Resolved handlers manage fetching
        }
    }

    const handleChange = () => {
        setChecked(!checked);
        if (!preferencesNoSplit) {
            changePreferences({ split: !checked });
        }
    }

    const densityHandler = useCallback(() => {
        tableRef.current?.onChangeDensity();
    }, [])

    /**
     * Retry handler for ErrorIndicator - re-fetches the failed page.
     */
    const handleRetry = useCallback(() => {
        getData({ newPage: page });
    }, [page, getData]);

    /**
     * Manual retry handler for total count badge when error occurs.
     */
    const handleRetryTotalCount = useCallback(() => {
        // Skip retry if total count is disabled
        if (!showTotalCount) return;
        if (!resolvedTotalCountError) return;
        
        // Build and execute total count query
        const totalQuery = buildGraphQLQuery({
            functionName: graphqlFunction,
            conditionsType: graphqlConditionsType,
            fields: graphqlTotalBaseField,
            extraFields: 'total',
        });
        const sanitizedConditions = conditions?.map(({ tags, account, ...condition }) => condition) || [];
        const mergedConditions = permanentConditions?.length
            ? [...sanitizedConditions, ...permanentConditions]
            : sanitizedConditions;
        const apiConditions = deduplicateConditions(mergedConditions);
        resolvedTotalFetchHandler({
            query: totalQuery,
            conditions: apiConditions,
            limit: 1,
            offset: 0,
        });
    }, [showTotalCount, resolvedTotalCountError, graphqlFunction, graphqlConditionsType, conditions, permanentConditions, resolvedTotalFetchHandler]);

    const conditionsHandler = (newconditions, newRows) => {
        setConditions(newconditions);
        let preferences = { conditions: newconditions }
        if (newRows)
            preferences.rows = newRows
        changePreferences(preferences);
    }

    const changePreferences = useCallback(async (newValues) => {
        if (name) {
            const oldPreferences = preferencesRef?.current?.getPreferences("Other", name) || {}
            if (!isEqual(newValues, oldPreferences)) {
                const currentPreferences = { ...oldPreferences, ...newValues }
                await preferencesRef.current.savePreference("Other", name, currentPreferences)
            }
        }
    }, [name])

    const handleColumnsChange = useCallback((newColums) => {
        onChangeColumns && onChangeColumns([...newColums]);
        let prefColums = newColums.map(x => {
            return { key: x?.key, show: x?.show, width: x?.width, locked: x?.locked, resize: x?.resize, icon: x?.icon, export: x?.export, functionString: x?.functionString, code: x?.code }
        })
        changePreferences({ columns: prefColums });
    }, [onChangeColumns, changePreferences])

    const handleSelectionChange = (items) => {
        setSelectedItems(items);
        onSelectedItems && onSelectedItems(items);
    }

    const cleanSelectedItems = () => {
        tableRef?.current?.setSelectedItems([]);
        onSelectedItems && onSelectedItems([]);
        setSelectedItems([]);
    }

    // Consolidated page reset helper
    const resetToFirstPage = () => {
        setPage(0);
        tableRef?.current?.setPage(1);
    };

    // Named event handlers extracted from JSX

    const handlePageChange = (newPage, rowsPerPage) => {
        // Handle page changes for both infinite scroll and pagination
        let pageIndex;
        // isLoadMore is true only for scroll-triggered infinite scroll loads
        const isLoadMore = infiniteScroll && newPage === undefined;
        if (isLoadMore) {
            // nextToken-driven load more: increment current page
            pageIndex = page + 1;
        } else {
            pageIndex = (newPage || 1) - 1;
        }

        if (onChangePage) {
            // Set isLoadingMore for onChangePage path
            setIsLoadingMore(isLoadMore);
            onChangePage(resolvedNextToken, newPage);
            setPage(pageIndex);
        } else {
            // For infinite scroll, isLoadMore is auto-calculated inside getData
            getData({ newPage: pageIndex });
        }
    };

    const handleRowsPerPageChange = (rows) => {
        if (onChangeRowsPerPage) {
            onChangeRowsPerPage(rows);
            setRows(rows);
        } else {
            getData({ newRows: rows });
        }

        changePreferences({ rows: rows });
    };

    const handleDensityChange = (density) => {
        setDensity(density);
    };

    /**
     * Normalize sort object to ensure consistent format across different layers.
     * Handles both UI format ({ key, order }) and query format ({ field, order }),
     * as well as array format (legacy).
     * 
     * IMPORTANT: This is the SINGLE SOURCE OF TRUTH for sort format conversion.
     * Always use this function when passing sort between layers:
     * - UI → API: normalizeSort(sort, 'query')
     * - API/defaults → UI: normalizeSort(sort, 'ui')
     * 
     * @param {Object|Array} sortInput - Sort object or array (accepts EITHER format)
     * @param {'ui'|'query'} format - Target format:
     *   - 'ui': { key, order: "asc"|"desc" } for VirtualTable, Preferences, component state
     *   - 'query': { field, order: "ASC"|"DESC" } for GraphQL API calls
     * @returns {Object|null} Normalized sort object, or null if invalid input
     */
    const normalizeSort = useCallback((sortInput, format) => {
        if (!sortInput) return null;
        
        // Handle array format (legacy or multi-sort)
        let sort = sortInput;
        if (Array.isArray(sortInput)) {
            console.warn('Sort is an array, extracting first element');
            sort = sortInput.length > 0 ? sortInput[0] : null;
            if (!sort) return null;
        }
        
        const key = sort.field || sort.key || 'id';
        const order = (sort.order || 'ASC').toUpperCase();
        
        if (format === 'ui') {
            // UI format: { key: "id", order: "asc" } (lowercase order)
            return { key, order: order.toLowerCase() };
        } else {
            // Query format: { field: "id", order: "ASC" } (uppercase order)
            return { field: key, order };
        }
    }, []);

    /**
     * Centralized sort update function - updates all state layers atomically.
     * Ensures parent state, child state, and persisted preferences stay in sync.
     * 
     * This function is the PREFERRED way to update sort state because it:
     * 1. Normalizes input to UI format for component state & VirtualTable
     * 2. Normalizes input to Query format for getData/GraphQL
     * 3. Persists UI format to preferences
     * 4. Returns Query format for immediate use in getData()
     * 
     * @param {Object} newSort - Sort object to apply (accepts EITHER format - will be normalized)
     * @returns {Object} Normalized QUERY format sort for getData ({ field, order: "ASC" })
     */
    const updateSort = useCallback((newSort) => {
        const normalizedUI = normalizeSort(newSort, 'ui');
        const normalizedQuery = normalizeSort(newSort, 'query');
        
        // Update all state layers atomically
        setSort(normalizedUI);
        tableRef?.current?.setSort?.(normalizedUI);
        changePreferences({ sort: normalizedUI });
        
        return normalizedQuery; // Return for getData
    }, [normalizeSort, changePreferences]);

    const handleSortChange = (sort) => {
        const querySort = updateSort(sort);
        
        // When onFetchRequest is configured, always call getData
        if (externalOnFetchRequest || !externalData) {
            getData({ newSort: querySort });
        } else if (onExternalSorting) {
            onExternalSorting(sort);
            tableRef?.current?.setPage(1);
        }
    };

    // Fix: search param lost at end of flow - now updates internal conditions
    const handleSearchChange = (tags, search, key) => {
        // Special case: last tag(s) removed — clear conditions, preferences and grid without firing a search
        if (search === 'clearGrid') {
            setTags([]);
            conditionsHandler([]);
            if (useCommonRedux && entity) {
                dispatch(clearEntity({ entity }));
            }
            // Always notify parent: in common Redux mode the parent can check for remaining
            // advanced filter conditions and re-search with them if needed
            onChangeConditions && onChangeConditions([], null, null, null, null, null, 'clearGrid');
            return;
        }

        let items = [];
        tags && tags.map((item, index) => {
            let obj = JSON.parse(JSON.stringify(item));
            if (typeof obj.id === "string") {
                obj.id = obj.id.replaceAll(",", " ")
                obj.id = obj?.id?.trim();
            }
            obj.name = obj?.name?.trim();
            items.push(obj);
        })

        if (items.length > searchTagsLimit) {
            items = items.slice(0, searchTagsLimit);
            const msg = (messages?.['common_dataview_search_limit_reached'] || 'Search limited to {max} items. Remove some items to add new ones.')
                .replace('{max}', searchTagsLimit);
            NotificationManager.warning(msg, messages?.['common_dataview_search_limit_title'] || 'Search Limit', 4000);
        }

        setTags(items);
        
        // Notify parent to transform tags into proper GraphQL conditions and trigger getData
        // Parent's onChangeConditions handles: tag→condition transformation, then calls
        // tableRef.getData({newConditions}) which internally calls conditionsHandler with
        // the properly formatted conditions (not raw tag objects)
        if (!search) {
            onChangeConditions && onChangeConditions(items, null, null, null, null, null, key);
        }
    };

    const exportHandler = (type, columnsVisibility) => {
        let currentType = type === 'CSV' ? 'Csv' : 'Pdf';
        if (exportGenerate) {
            exportGenerate(currentType, columnsVisibility)
        } else {
            if (!exportUseDataGrid) {
                let headers = []
                const safeColumns = Array.isArray(columns) ? columns : [];

                if (columnsVisibility === 'All')
                    headers = safeColumns.filter(x => x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } });
                else
                    headers = safeColumns.filter(x => x.show && x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } });

                // Build export-specific query with 'total' instead of 'nextToken'
                let exportParams = graphqlCustomQuery || "id";
                if (graphqlDynamicQuery) {
                    exportParams = buildDynamicFields(columns, exportParams, columnsVisibility === 'All');
                }
                let query = buildGraphQLQuery({
                    functionName: graphqlFunction,
                    conditionsType: graphqlConditionsType,
                    sortType: graphqlSortType,
                    fields: exportParams,
                    extraFields: 'total',
                });
                // Deduplicate conditions for export (same as getData does)
                const sanitizedConditions = conditions?.map(({ tags, account, ...condition }) => condition) || [];
                const apiConditions = deduplicateConditions(sanitizedConditions);
                let currentFilters = {
                    conditions: apiConditions,
                    limit: rows,
                    offset: 0,
                    sort: [normalizeSort(sort, 'query')].filter(Boolean),
                }

                dispatch(genericExportSend(
                    currentType,
                    query,
                    currentFilters,
                    headers,
                    { unit: 'pt', size: 'A4', orientation: 'portrait', fontSize: 12 },
                    exportIsPXSource,
                    exportIsDynamo,
                    exportPxAccount,
                    exportFileName,
                    null,
                    exportLimit,
                ));
            } else {
                exportCSV(resolvedData, columnsVisibility);
            }
        }

    }


    const exportCSV = (data, columnsVisibility = 'All') => {

        let csvContent = '';
        const headerRow = [];
        let show = false
        columns &&
            columns.forEach((item) => {
                if (item.export) headerRow.push(item.title || item.field);
            });

        csvContent += headerRow.join(',') + '\n';

        data.forEach((item) => {
            const row = [];
            columns &&
                columns.forEach((col) => {
                    show = false
                    if (columnsVisibility === 'all')
                        show = true
                    else
                        show = col.show
                    if (col.export && show) {
                        let val = '';
                        val = _.get(item, col.exportCell);
                        val = val !== -1 ? val : ''

                        if (!col.exportCell && col.exportValue) {
                            val = col.exportValue(item)
                        }

                        if (col.exportFormat && val) {
                            let timezone = item?.timezone;
                            const validDate = validateEpochDate(val)
                            val = validDate ? formatExportValue(val, col.exportFormat, timezone) : ''
                        }

                        if ((col.field === 'UpdatedBy' || col.field === 'CreatedBy') && val)
                            val = getPMUser(val, pmUsers);

                        row.push(val || '');
                    }
                });

            csvContent += row.join(',') + '\n';

        });

        var a = document.createElement('a');
        let mimeType = 'text/csv;encoding:utf-8';
        let fileName = buildExportFileName(name, 'csv');

        if (URL && 'download' in a) {
            // HTML5 A[download]
            a.href = URL.createObjectURL(new Blob([csvContent], { type: mimeType }));
            a.setAttribute('download', fileName);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            location.href = 'data:application/octet-stream,' + encodeURIComponent(csvContent);
        }
    }

    useImperativeHandle(ref, () => ({
        getData: (...args) => {
            return getData(...args);
        },
        conditions: conditions,
        conditionsHandler: conditionsHandler,
        setTags: setTags,
        setValueSearch: setValueSearch,
        cleanSelectedItems: cleanSelectedItems,
        selectedItems: selectedItems,
        setSort: setSort,
        setRows: setRows,
        // Batch sequential: mark processed items so next "Select All" skips them
        markAsProcessed: (items) => {
            tableRef?.current?.markAsProcessed(items);
        },
        // Reset processed tracking (auto-called on filter/search change)
        resetProcessed: () => {
            tableRef?.current?.resetProcessed();
        },
        clearGrid: () => {
            setTags([]);
            conditionsHandler([]);
            if (useCommonRedux && entity) {
                dispatch(clearEntity({ entity }));
            }
        },
    }));

    // Prop enforcement for onFetchRequest + infiniteScroll mode
    useEffect(() => {
        if (infiniteScroll && externalOnFetchRequest) {
            if (!externalData) {
                console.error(
                    '[DataViewInfiniteScroll] When using infiniteScroll with onFetchRequest, ' +
                    'externalData prop is required. DataViewInfiniteScroll delegates fetching to parent ' +
                    'via onFetchRequest, so parent must provide data via externalData.'
                );
            }
            if (externalLoading === undefined) {
                console.warn(
                    '[DataViewInfiniteScroll] externalLoading prop is recommended when using ' +
                    'infiniteScroll with onFetchRequest for proper loading states.'
                );
            }
        }
    }, [infiniteScroll, externalOnFetchRequest, externalData, externalLoading]);

        // Debouncer for search input - waits 500ms before applying search condition
    useEffect(() => {
        if (valueSearch && !searchNoAutomatic) {
            const timer = setTimeout(() => {
                tableRef.current.setPage(1)
                tableRef.current.setSelectedItems([]);
                let currenttags = tags;
                if (!searchNoTags) {
                    if (valueSearch) onChangeConditions && onChangeConditions([...currenttags, valueSearch]);
                    else onChangeConditions && onChangeConditions([...currenttags]);
                }
            }, SEARCH_DEBOUNCE_MS)
            return () => clearTimeout(timer);
        }
    }, [valueSearch])

    useEffect(() => {
        if (!infiniteScroll && ((externalData && externalTotal >= 0))) {
            onChangeTotal && onChangeTotal(externalTotal);
        }

        if (errorDataView) {
            showError && showError(errorDataView);
            dispatch(clearRedux("COMMON", { errorDataView: null }))
        }

    }, [externalTotal, errorDataView])

    // i18n: Re-translate column headers when language changes
    // The `change` flag is set to true by COMMON_CHANGE_SELECT_LANGUAGE Redux action
    useEffect(() => {
        if (change) {
            let columnsTranslated = columns.map((column) => ({ ...column, title: column.code ? messages[column.code] : column.title }))
            handleColumnsChange(columnsTranslated);
            dispatch(clearRedux("COMMON", { change: false }))
        }
    }, [change, messages, columns, handleColumnsChange, dispatch])

    // Initialize entity state in common Redux on mount
    useEffect(() => {
        if (useCommonRedux) {
            dispatch(initEntity({ entity }));
        }
    }, [useCommonRedux, entity, dispatch]);

    // Cleanup entity state and abort in-flight requests on unmount (common Redux mode)
    useEffect(() => {
        return () => {
            if (useCommonRedux) {
                // Option C: Abort any in-flight request for this entity on unmount
                abortEntityRequest(entity);
                
                // Clear entity state if clearOnUnmount is not explicitly set to false
                if (clearOnUnmount !== false) {
                    dispatch(clearEntity({ entity }));
                }
            }
        };
    }, [useCommonRedux, entity, dispatch, clearOnUnmount]);

    // Track showTotalCount transitions for badge placeholder/persisted state.
    // When showTotalCount transitions false → true (e.g., Last → Range mode switch):
    //   - If getData() was NOT called while hidden → keep persisted total count (data unchanged)
    //   - If getData() WAS called while hidden → show pending icon, then auto-fetch total count
    // When views call applyFilters(value) synchronously from setType, getData() fires before
    // React updates showTotalCount. This useEffect catches the transition and fires the total
    // count query that getData() couldn't (because showTotalCount was still stale/false).
    useEffect(() => {
        const wasDisabled = prevShowTotalCountRef.current === false;
        const isNowEnabled = showTotalCount === true;
        const wasEnabled = prevShowTotalCountRef.current === true;
        const isNowDisabled = showTotalCount === false;
        prevShowTotalCountRef.current = showTotalCount;

        if (wasDisabled && isNowEnabled && !isInitialLoadRef.current) {
            // Only show pending if data actually changed while badge was hidden
            if (dataFetchedWhileHiddenRef.current) {
                setIsTotalCountPending(true);   // Show placeholder badge — total is stale
                if (useCommonRedux) {
                    dispatch(resetEntityTotalCount({ entity }));
                }

                // Auto-fetch total count: getData() was called while showTotalCount was false
                // (stale React state during synchronous setType → applyFilters flow), so the
                // total count query was skipped. Fire it now that showTotalCount is updated.
                const totalQuery = buildGraphQLQuery({
                    functionName: graphqlFunction,
                    conditionsType: graphqlConditionsType,
                    fields: graphqlTotalBaseField,
                    extraFields: 'total',
                });
                const apiConditions = conditions?.map(({ tags, ...condition }) => condition) || [];
                resolvedTotalFetchHandler({
                    query: totalQuery,
                    conditions: apiConditions,
                    limit: 1,
                    offset: 0,
                });
                setIsTotalCountPending(false); // Real fetch starting, clear placeholder
            }
            // Otherwise: keep persisted resolvedTotalCount — data never changed
            dataFetchedWhileHiddenRef.current = false; // Reset tracking for next cycle
        }
        if (wasEnabled && isNowDisabled) {
            setIsTotalCountPending(false);  // Clear when badge is hidden
            dataFetchedWhileHiddenRef.current = false; // Reset tracking
        }
    }, [showTotalCount]);

    // Preference sub-functions — break 88-line callback into focused, testable units

    // Apply column preferences: show/hide, order, width, locked, etc.
    const applyColumnPreferences = (preferences) => {
        if (preferencesDisabled || !preferences?.columns) return null;
        let newColumns = [...columns];
        let reset = false;
        preferences.columns.forEach((col, index) => {
            let indexCol = newColumns.findIndex(x => (x?.key == col?.key));
            if (indexCol === -1) {
                reset = true;
                return;
            }
            let newCol = { ...newColumns[indexCol], ...col };
            newCol.key = newColumns[indexCol].key;
            newCol.field = newColumns[indexCol].field;
            // functionString needs to be read always from the actual column, not from preferences
            newCol.functionString = newColumns[indexCol].functionString;
            newCol.render = newColumns[indexCol].render;
            newCol.title = newColumns[indexCol].title;
            newCol.code = newColumns[indexCol].code;
            newColumns.splice(indexCol, 1);
            newColumns.splice(index, 0, newCol);
        });
        if (reset) newColumns = [...columns];
        // repositioning columns
        let positionColumns = newColumns.filter((item) => item?.position);
        positionColumns?.forEach((colum) => {
            let indexCol = newColumns.findIndex(x => (x?.key == colum?.key));
            if (indexCol !== -1) {
                newColumns.splice(indexCol, 1);
                newColumns.splice(colum.position, 0, colum);
            }
        });
        onChangeColumns && onChangeColumns(newColumns);
        return newColumns?.filter((item) => item?.show);
    };

    const applyDensityPreferences = (preferences) => {
        if (preferencesDisabled || !preferences?.density) return;
        setDensity(preferences.density);
        tableRef?.current?.onChangeDensity(preferences.density);
    };

    const applyConditionPreferences = (preferences) => {
        if (preferencesDisabled || !preferences?.conditions) return [];
        
        let prefConditions = preferences.conditions;
        
        // Support both array-shaped (standard) and object-shaped (legacy vidfleet tabs) conditions
        // Vidfleet troubleshooting tabs save filter payloads as objects, not arrays
        if (!Array.isArray(prefConditions) && typeof prefConditions === 'object') {
            // Keep object as-is for parent handlers that expect object when fromPreferences === true
            setConditions(prefConditions);
            
            // Restore tags UI if present in the saved object
            if (prefConditions.tags && Array.isArray(prefConditions.tags) && prefConditions.tags.length > 0) {
                setTags(prefConditions.tags);
            }
            
            // Return object to caller - parent's onChangeConditions will receive it with fromPreferences=true
            return prefConditions;
        }
        
        // Standard array handling below
        if (preferencesNoTags) {
            prefConditions = preferences.conditions.filter(x => !x.tags);
            prefConditions = prefConditions.filter(x => x.fields && x.fields[0] !== 'status');
            prefConditions = prefConditions.filter(x => x.fields && x.fields[0] !== 'unitTypeId');
        } else if (searchFields) {
            prefConditions = preferences.conditions.filter(x => !x.tags);
            let tagsConditions = preferences.conditions.filter(x => x.tags);
            if (tagsConditions.length > 0) {
                if (tagsConditions[0].fields.length > 1)
                    tagsConditions[0].fields = searchFields;
                prefConditions.push(tagsConditions[0]);
            }
        }
        setConditions(prefConditions);
        
        // FIX: Also restore visual tags from preferences
        // Tags condition stores full tag objects in the `tags` property (not `terms`)
        // Structure: { fields: [...], terms: [...], tags: [{id, label}, ...], conditionOperator: "OR" }
        const tagsCondition = preferences.conditions.find(x => x.tags);
        if (tagsCondition?.tags?.length > 0) {
            // The `tags` property contains the original tag objects with id/label
            setTags(tagsCondition.tags);
        }
        
        return prefConditions;
    };

    const applySplitPreferences = (preferences) => {
        if (preferencesDisabled || preferencesNoSplit || !preferences?.split) return;
        setChecked(preferences.split);
    };

    const applyRowPreferences = (preferences) => {
        let finalRows = defaultsRowsPerPage;
        if (!preferencesDisabled && !infiniteScroll && preferences?.rows) {
            // Only restore persisted rows in pagination mode. In infinite scroll mode the
            // rows-per-page selector is hidden, so a stale preference value (e.g. 300 from
            // the legacy DataView) would silently inflate the API limit with no way to reset it.
            setRows(preferences.rows);
            tableRef?.current?.setRowsPerPage(preferences.rows);
            finalRows = preferences.rows;
        } else if (defaultsRowsPerPage) {
            setRows(defaultsRowsPerPage);
            tableRef?.current?.setRowsPerPage(defaultsRowsPerPage);
            finalRows = defaultsRowsPerPage;
        }

        return finalRows
    };

    /**
     * Apply sort preferences with proper normalization.
     * Handles both UI format ({ key, order }) and query format ({ field, order }),
     * as well as legacy array format - normalizes to consistent UI format.
     */
    const applySortPreferences = (preferences) => {
        if (preferencesDisabled || !preferences?.sort) return;
        
        let sortToApply = preferences.sort;
        
        // Handle array format (legacy or corrupted preferences) - Fix D
        if (Array.isArray(sortToApply)) {
            console.warn('Preferences contained array sort, normalizing to object');
            sortToApply = sortToApply.length > 0 ? sortToApply[0] : null;
        }
        
        if (!sortToApply) {
            // Fallback to default if sort is invalid
            sortToApply = defaultsSort;
        }
        
        // Normalize to UI format for consistent state - Fix B
        const normalizedUI = normalizeSort(sortToApply, 'ui');
        
        setSort(normalizedUI);
        tableRef?.current?.setSort?.(normalizedUI);
    };

    const applyInitialPreferences = (preferences) => {
        // Skip fetch if data already exists in Redux (component remounted with clearOnUnmount=false)
        // This prevents unnecessary refetch when navigating back to a view that persisted its data
        const hasPersistedData = useCommonRedux && commonReduxEntityState?.items?.length > 0;
        
        if (hasPersistedData) {
            // Data persisted from previous mount - apply UI preferences only, skip data fetch
            // Apply all UI preferences for consistent state restoration
            applyColumnPreferences(preferences);
            applyDensityPreferences(preferences);
            const prefConditions = applyConditionPreferences(preferences);  // Restore search tags UI state
            applySplitPreferences(preferences);
            const prefRows = applyRowPreferences(preferences);
            applySortPreferences(preferences);
            // Notify parent to restore filter states (type/last/dates) WITHOUT fetching data.
            // restoreOnly=true (9th param) tells parent to sync UI state only, skip getData().
            if (!preferencesDisabled && onChangeConditions) {
                onChangeConditions(null, prefConditions, 0, null, prefRows, preferences?.sort, null, true, true);
            }
            return;
        }
        
        // Normal flow for initial load (no persisted data)
        const visibleColumns = applyColumnPreferences(preferences);
        applyDensityPreferences(preferences);
        const prefConditions = applyConditionPreferences(preferences);
        applySplitPreferences(preferences);
        const prefRows = applyRowPreferences(preferences);
        applySortPreferences(preferences);
        if (!preferencesDisabled) {
            //call prefRows instead of preferences?.rows to handle limit fixed on infiniteScroll mode
            onChangeConditions && onChangeConditions(null, prefConditions, 0, visibleColumns, /* preferences?.rows */ prefRows, preferences?.sort, null, true);
        }
    };

    return (
        <Preferences
            ref={preferencesRef}
            preferencesKey={name}
            onInitialPreferences={applyInitialPreferences}
        >
            <Grid 
                container 
                className="container-dataview container-dataview-is"
                rowGap={"0.6em"}
            >
                <Grid 
                    item 
                    // lg={(!resolvedShowFirstFilters && !extraButton) ? 12 : !showTotalCount ? 3 : 4} 
                    md={(!resolvedShowFirstFilters && !extraButton) ? 12 : resolvedTopTableGridWidths.tableButtons} 
                    sm={12} 
                    xs={12}
                    style={{alignContent: "end"}}
                >
                    <div 
                        className="container-dataview-buttons"
                        //no wrap only activates when prop fixes it to false OR we are not showing ButtonOptions NOR totalCount. Maintain UI structure in these cases
                        style={{
                            ...(!uiButtonsFlexWrap ? {flexWrap: "nowrap"} : (!resolvedTopTableGridWidths?.hasOptions && !showTotalCount) && {flexWrap: "nowrap"} )
                        }}
                    >
                        {
                            options?.length > 0 &&
                            <MultiLevelSelector
                                label={messages['actions']}
                                options={options || []}
                                selectedItems={selectedItems}
                                position="right"
                            />
                        }
                        {!uiHideFilters &&
                            <Tooltip title={messages['advanced_filters']}>
                                <IconButton
                                    style={FILTER_BUTTON_STYLE}
                                    onClick={() => onFilters && onFilters()}
                                    size="large">
                                    <FilterIcon style={ICON_STYLE} className={uiFilterSelected ? "button-filter-selected" : "button-filter-no-selected"} />
                                </IconButton>
                            </Tooltip>
                        }

                        {
                            !uiHideRefresh &&
                            <Tooltip title={messages['refresh']}>
                                <IconButton
                                    style={TOOLBAR_BUTTON_STYLE}
                                    onClick={() => {
                                        if (externalOnRefresh) {
                                            externalOnRefresh();
                                        } else {
                                            getData(infiniteScroll ? { newPage: 0 } : {});
                                        }
                                    }}
                                    size="large">
                                    <RefreshIcon style={ICON_STYLE} />
                                </IconButton>
                            </Tooltip>
                        }
                        {
                            !uiHideDensity &&
                            <Tooltip title={messages['density']}>
                                <IconButton
                                    style={DENSITY_BUTTON_STYLE}
                                    onClick={() => {
                                        let currentDensity = tableRef.current?.density;
                                        let value = currentDensity === 3 ? 1 : currentDensity + 1;
                                        changePreferences({ density: value });
                                        densityHandler();
                                    }}
                                    size="large">
                                    <DensityIcon style={ICON_STYLE} />
                                </IconButton>
                            </Tooltip>
                        }
                        <TableButtons
                            lockedColumns
                            messages={messages}
                            buttons={tableButtons || []}
                            columns={columns || []}
                            onChangeColumns={(items, show) => {
                                // Check if the currently sorted column was just hidden
                                // Support both UI format (sort.key) and query format (sort.field)
                                const currentSortKey = sort?.key || sort?.field;
                                const sortedColumnHidden = !show && currentSortKey && !items.find(
                                    col => col.show && (col.field === currentSortKey || col.key === currentSortKey)
                                );

                                if (sortedColumnHidden) {
                                    // FIX: Race condition — previously updateSort() and handleColumnsChange()
                                    // each called changePreferences() separately in the same synchronous tick.
                                    // The second call read stale preferences (before React re-rendered with the
                                    // first call's optimistic Redux update), overwriting the sort fallback.
                                    // Solution: update sort state directly, then save sort + columns in ONE
                                    // atomic changePreferences() call so there's only one read of oldPreferences.
                                    const normalizedUI = normalizeSort(defaultsSort, 'ui');
                                    const normalizedQuery = normalizeSort(defaultsSort, 'query');

                                    // Update sort state layers directly (bypass updateSort to avoid its changePreferences call)
                                    setSort(normalizedUI);
                                    tableRef?.current?.setSort?.(normalizedUI);

                                    // Re-fetch with fallback sort
                                    getData({ newColumns: items, newSort: normalizedQuery, newPage: 0 });

                                    // Notify parent of column change (same as handleColumnsChange does)
                                    onChangeColumns && onChangeColumns([...items]);

                                    // ATOMIC: persist both sort AND columns in a single call — avoids race
                                    let prefColumns = items.map(x => {
                                        return { key: x?.key, show: x?.show, width: x?.width, locked: x?.locked, resize: x?.resize, icon: x?.icon, export: x?.export, functionString: x?.functionString, code: x?.code }
                                    })
                                    changePreferences({ sort: normalizedUI, columns: prefColumns });
                                } else {
                                    // Normal path: no sort change involved, so no race condition possible
                                    if (show && graphqlDynamicQuery) {
                                        // REFRESH: reset to page 0 so all rows have the new field
                                        // (dynamicQuery builds GraphQL fields from visible columns, so showing a column requires full refresh)
                                        getData({ newColumns: items, newPage: 0 });
                                    }
                                    handleColumnsChange(items);
                                }
                            }}
                            tableRef={tableRef}
                            noPDF
                            externalExport={(event, columnsVisibility) => exportHandler(event, columnsVisibility)}
                        />
                        {/* Total count badge - shows when totalCount prop is provided and showTotalCount is enabled */}
                        {showTotalCount && (
                            <Tooltip
                                title={resolvedTotalCountError ? (messages['common_dataview_total_retry'] || 'Click to retry') : ''}
                                disableHoverListener={!resolvedTotalCountError}
                            >
                                <div 
                                    style={{
                                        ...TOTAL_BADGE_WRAPPER_STYLE,
                                        cursor: resolvedTotalCountError ? 'pointer' : 'default',
                                    }}
                                    onClick={resolvedTotalCountError ? handleRetryTotalCount : undefined}
                                >
                                    <Chip
                                        label={
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                {/* Show error icon when total query failed */}
                                                {resolvedTotalCountError && (
                                                    <ErrorOutlineIcon sx={{ fontSize: 16 }} />
                                                )}
                                                {messagesTotalLabel || 'Total'}: {
                                                    (resolvedTotalCountLoading || loading)
                                                    ? <CircularProgress size={16} thickness={4} sx={{ color: '#FFFFFF' }} />
                                                    : (isTotalCountPending || resolvedTotalCount === null)
                                                        ? <HourglassEmptyIcon data-testid="total-count-placeholder" sx={{ fontSize: 16 }} />
                                                        : resolvedTotalCountError
                                                            ? '0' // Show zero when error (not cached value)
                                                            : resolvedTotalCount.toLocaleString()
                                                }
                                            </span>
                                        }
                                        sx={{
                                            // Error state styling - amber/warning color
                                            backgroundColor: resolvedTotalCountError ? '#f57c00' : '#616161',
                                            color: '#FFFFFF',
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            '&:hover': resolvedTotalCountError ? {
                                                backgroundColor: '#ef6c00',
                                            } : {},
                                        }}
                                    />
                                </div>
                            </Tooltip>
                        )}
                    </div>
                </Grid>
                <Grid 
                    item 
                    // lg={!showTotalCount ? 9 : 8} 
                    md={(!resolvedShowFirstFilters && !extraButton) ? 12 : resolvedTopTableGridWidths.restFilters} 
                    sm={12} 
                    xs={12} 
                    style={JUSTIFY_END_STYLE}
                >
                    <div
                    style={{ 
                        width: "100%",
                        display: !uiFlexEnd ? 'flex' : 'flex-end', 
                        alignItems: searchFixBar ? 'center' : '', 
                        justifyContent: 'end',
                        flexWrap: 'wrap', 
                        rowGap: "0.6em", 
                    }}
                    >
                        {
                            !uiHideSplit &&
                            <div className="split-action">
                                <ToggleSwitch
                                    id="splitAction"
                                    large
                                    blueToggle
                                    optionLabels={[messages['split'], messages['no_split']]}
                                    checked={checked}
                                    onChange={handleChange}
                                />
                            </div>
                        }
                        {
                            extraFilters && extraFilters()
                        }
                        {
                            !searchDisabled &&
                            <div 
                            style={{ 
                                /* width: searchBarWidth ? `${searchBarWidth}%` : "70%", */ 
                                flex: "1 1 200px", maxWidth: 500, minWidth: 200, 
                                zIndex: 4, 
                                display: 'flex', 
                                alignItems: 'flex-end' 
                            }}
                                >
                                <SearchInput
                                    graphqlFunction={graphqlFunction}
                                    graphqlConditions={graphqlConditionsType}
                                    searchConditions={searchConditions}
                                    searchOptionValue={searchOptionValue}
                                    searchOptionLabel={searchOptionLabel}
                                    customSearch={searchCustom}
                                    extraField={searchExtraField}
                                    value={tags}
                                    onChange={handleSearchChange}
                                    noSplit={uiHideSplit}
                                    checked={checked}
                                    placeholder={searchPlaceholder || messages['search']}
                                    messages={messages}
                                    style={searchStyle}
                                    isDisabled={resolvedLoading || loading || loadingPreference}
                                    noSearchOptions={searchNoOptions}
                                    conditions={conditions}
                                    conditionsHandler={conditionsHandler}
                                    openSearchCustomOptions={searchOpenCustomOptions}
                                    onlyEnterSearch={searchOnlyEnter}
                                    clearGridOnEmpty={searchClearGridOnEmpty}
                                    splitNoDevices={uiSplitNoDevices}
                                />
                            </div>
                        }
                        {
                            searchSimple &&
                            <InputTags
                                tags={tags}
                                setTags={(tags) => {
                                    if (!searchNoTags) {
                                        onChangeConditions && onChangeConditions(tags);
                                        setTags(tags);
                                    }
                                }}
                                placeholder={searchPlaceholder || messages['search']}
                                valueSearch={valueSearch}
                                setValueSearch={(value) => {
                                    onChangeSearch && onChangeSearch(value)
                                    setValueSearch(value)
                                }}
                                noClear
                            />
                        }
                        {
                            extraButton && extraButton()
                        }
                    </div>
                </Grid>
                <Grid item xs={12} style={TABLE_WRAPPER_STYLE}>
                    <Table
                        ref={tableRef}
                        
                        // DATA CONFIG: Core data state for table rendering
                        dataConfig={{
                            data: resolvedData.length > 0 ? resolvedData : (data?.items || []),
                            loading: isLoadingMore ? false : (resolvedLoading || loading || loadingPreference),
                            isLoadingMore,
                            nextToken: resolvedNextToken,  // Infinite-scroll indicator for load-more trigger
                            total: externalTotal || resolvedTotalCount || 0,
                            moreRowsAvailable: infiniteScroll ? !!resolvedNextToken : undefined,
                            error: resolvedError // Pass resolvedError to Table for displaying error state in infinite scroll mode,
                        }}

                        // TABLE CONFIG: Column definitions and structure settings
                        tableConfig={{
                            columns,
                            keyColumn,  // Unique row identifier for selection and virtualization
                            lockedColumns: true,  // Prevent column reordering beyond locked position
                            customColumn,
                            menuOptions,
                            onChangeColumns: handleColumnsChange,
                        }}
                        
                        // UI CONFIG: Layout and appearance settings
                        uiConfig={{
                            topHeight: uiTopHeight,
                            height: uiHeight,
                            hidePagination: infiniteScroll ? true : uiHidePagination,
                            hideAllInPagination: infiniteScroll ? true : uiHideAllInPagination,
                            defaultDensity: density,
                        }}

                        // SELECTION CONFIG: Row selection behavior and limits
                        selectionConfig={{
                            disabled: selectionDisabled,
                            maxItems: selectionMaxItems,
                            showCounter: selectionShowCounter,
                            onSelectedItems: handleSelectionChange,
                            onSelectionLimitReached: selectionOnLimitReached,
                        }}
                        
                        // PAGING CONFIG: Pagination and infinite scroll settings
                        pagingConfig={{
                            infiniteScroll,
                            valuesPerPage: defaultsValuesPerPage,
                            onChangePage: handlePageChange,
                            onChangeRowsPerPage: handleRowsPerPageChange,
                            // Retry callback for ErrorIndicator
                            onRetry: handleRetry,
                            // Sync VirtualTable displayed page (1-indexed) with parent page state
                            currentPage: page + 1,
                        }}
                        
                        // MESSAGES CONFIG: i18n messages for empty states and selection UI
                        messagesConfig={{
                            ...messagesConfig,
                            noMoreItemsMessage: messagesNoMoreItems || messages['no_more_items'],
                            emptyConfig,
                            emptyType: computedEmptyType,
                            // i18n: Pass resolved messages with translated strings
                            messages: {
                                ...messagesConfig?.messages,
                                // Empty state messages (used by TableEmptyState)
                                initial: messagesEmptyInitial,
                                noData: messagesEmptyNoData,
                                filteredEmpty: messagesEmptyFiltered,
                                searchEmpty: messagesEmptySearch,
                                error: messagesEmptyError,
                                // Selection messages (used by header.js and SelectionCounter)
                                selectionLimitReached: messagesSelectionLimitReached,
                                selectionLimitTitle: messagesSelectionLimitTitle,
                                selectionLimitExceeded: messagesSelectionLimitExceeded,
                                selectionLimitTooltip: messagesSelectionLimitTooltip,
                                // Drag tooltip message (used by header.js)
                                dragToReorder: messagesDragToReorder,
                                // Total label (used by footer/pagination)
                                totalLabel: messagesTotalLabel,
                                // ErrorIndicator messages
                                errorLoadingMore: messages['common_dataview_error_loading_more'],
                                retry: messages['common_dataview_retry'],
                            }
                        }}
                        
                        // CALLBACKS: Event handlers for table interactions (flat props)
                        onChangeSort={handleSortChange}
                        onClickMenu={onClickMenu}
                        onChangeDensity={handleDensityChange}
                        
                        // Pass virtualization config for customizing table performance
                        virtualizationConfig={virtualizationConfig}
                        />
                </Grid>
            </Grid>
        </Preferences>
    );
})

// ════════════════════════════════════════════════════════════════════════════
// PROP TYPES VALIDATION
// ════════════════════════════════════════════════════════════════════════════

/**
 * GraphQL configuration for data fetching.
 * Required when NOT using externalDataConfig (Common Redux mode).
 * 
 * @required function - GraphQL query function name
 * @required conditionsType - GraphQL conditions input type for filtering
 * @optional sortType - GraphQL sort input type (defaults to no sorting)
 * @optional customQuery - Custom fields to query (overrides default)
 * @optional dynamicQuery - Build query from visible columns
 */
const graphqlConfigShape = PropTypes.shape({
    /** GraphQL function name (e.g., 'searchUnits'). REQUIRED for GraphQL mode. */
    function: PropTypes.string.isRequired,
    /** GraphQL conditions input type (e.g., 'UnitSearchInput'). REQUIRED for GraphQL mode. */
    conditionsType: PropTypes.string.isRequired,
    /** GraphQL sort input type (e.g., 'UnitSortInput'). Optional - enables sorting. */
    sortType: PropTypes.string,
    /** Custom fields to query (e.g., 'id name esn'). Optional - overrides default query. */
    customQuery: PropTypes.string,
    /** Build query dynamically from visible columns. Optional. @default false */
    dynamicQuery: PropTypes.bool,
    /** Base field to query for total count (e.g., 'id', 'esn'). @default 'id' */
    totalBaseField: PropTypes.string,
});

/**
 * External data configuration for parent-managed data fetching.
 * Use this when the parent component manages data (React Query, Apollo, custom Redux).
 * 
 * @required data - Array of items to display
 * @required onFetchRequest - Callback when component needs data
 * @optional loading, nextToken, total, etc. - State indicators
 */
const externalDataConfigShape = PropTypes.shape({
    /** Array of data items to display. REQUIRED for external data mode. */
    data: PropTypes.array.isRequired,
    /** Callback when component needs to fetch data. REQUIRED for external data mode.
     *  Signature: ({ query, variables, isLoadMore }) => void */
    onFetchRequest: PropTypes.func.isRequired,
    /** Loading state indicator. Recommended. @default false */
    loading: PropTypes.bool,
    /** Pagination cursor for infinite scroll. Required for infinite scroll pagination. */
    nextToken: PropTypes.string,
    /** Total count for non-infinite scroll pagination. */
    total: PropTypes.number,
    /** Total count for infinite scroll badge display. */
    totalCount: PropTypes.number,
    /** Loading state for total count query. @default false */
    totalCountLoading: PropTypes.bool,
    /** Error message for total count query (enables retry). @default null */
    totalCountError: PropTypes.string,
    /** Callback for total count fetch. Signature: ({ query, conditions, sort }) => void */
    onFetchTotalRequest: PropTypes.func,
    /** Custom refresh handler. Optional. */
    onRefresh: PropTypes.func,
    /** Error message from data fetch (shows error empty state). @default null */
    error: PropTypes.string,
});

/**
 * Search configuration for search bar behavior.
 * 
 * CRITICAL: When search is enabled (disabled !== true) and autocomplete is used
 * (noOptions !== true), you MUST provide optionValue and optionLabel to avoid
 * malformed GraphQL queries with undefined fields.
 * 
 * See: docs/BUG-ANALYSIS-undefined-query-fields.md
 */
const searchConfigShape = PropTypes.shape({
    /** Disable search bar entirely. @default false */
    disabled: PropTypes.bool,
    /** Placeholder text for search input. */
    placeholder: PropTypes.string,
    /** Width of search bar as percentage (1-100). @default 70 */
    barWidth: PropTypes.number,
    /** Search conditions - fields to include in search query. */
    conditions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    /** 
     * Field name for autocomplete option value (e.g., 'id').
     * REQUIRED when using autocomplete (noOptions !== true).
     * Without this, GraphQL query will have 'undefined' fields.
     */
    optionValue: PropTypes.string,
    /** 
     * Field name for autocomplete option label (e.g., 'name', 'label').
     * REQUIRED when using autocomplete (noOptions !== true).
     * Without this, GraphQL query will have 'undefined' fields.
     */
    optionLabel: PropTypes.string,
    /** Custom search component/function. */
    custom: PropTypes.func,
    /** Extra field to include in search query results. */
    extraField: PropTypes.string,
    /** Disable automatic search on type. @default false */
    noAutomatic: PropTypes.bool,
    /** Disable autocomplete dropdown options. @default false */
    noOptions: PropTypes.bool,
    /** Array of field names to search across. */
    fields: PropTypes.arrayOf(PropTypes.string),
    /** Open custom options by default. @default false */
    openCustomOptions: PropTypes.bool,
    /** Only trigger search on Enter key. @default false */
    onlyEnter: PropTypes.bool,
    /** Use simple InputTags mode. @default false */
    simple: PropTypes.bool,
    /** Disable tag-based search. @default false */
    noTags: PropTypes.bool,
    /** Custom styles for search bar. */
    style: PropTypes.object,
    /** Fix search bar position. @default false */
    fixBar: PropTypes.bool,
    /** Maximum autocomplete results to fetch. @default 30 */
    limit: PropTypes.number,
});

/**
 * Export configuration for CSV/PDF exports.
 * All properties are optional with sensible defaults.
 */
const exportConfigShape = PropTypes.shape({
    /** Base file name for exports (timestamp appended). */
    fileName: PropTypes.string,
    /** Maximum rows to export. @default 100 */
    limit: PropTypes.number,
    /** Use DataGrid export instead of custom. @default false */
    useDataGrid: PropTypes.bool,
    /** Custom export generator function. */
    generate: PropTypes.func,
    /** Data from PX source. @default false */
    isPXSource: PropTypes.bool,
    /** PX account identifier. */
    pxAccount: PropTypes.string,
    /** Data from DynamoDB source. @default false */
    isDynamo: PropTypes.bool,
});

/**
 * UI configuration for layout and visibility toggles.
 * All properties are optional with sensible defaults.
 */
const uiConfigShape = PropTypes.shape({
    /** Top offset for table height calculation (pixels). */
    topHeight: PropTypes.number,
    /** Override table height (pixels or CSS string). */
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Hide filter button. @default false */
    hideFilters: PropTypes.bool,
    /** Hide refresh button. @default false */
    hideRefresh: PropTypes.bool,
    /** Hide density toggle. @default false */
    hideDensity: PropTypes.bool,
    /** Hide pagination controls. @default false */
    hidePagination: PropTypes.bool,
    /** Hide split view toggle. @default false */
    hideSplit: PropTypes.bool,
    /** Toolbar spans full width. @default false */
    toolbarFullWidth: PropTypes.bool,
    /** Flex-end alignment for toolbar. @default false */
    flexEnd: PropTypes.bool,
    /** Filter button selected/active state. @default false */
    filterSelected: PropTypes.bool,
    /** Special split behavior for no devices. @default false */
    splitNoDevices: PropTypes.bool,
    /** Table top-level buttons with flex wrap. @default true */
    buttonsFlexWrap: PropTypes.bool,
});

/**
 * Selection configuration for row selection behavior.
 * All properties are optional with sensible defaults.
 */
const selectionConfigShape = PropTypes.shape({
    /** Disable row selection entirely. @default false */
    disabled: PropTypes.bool,
    /** Maximum selectable items (for bulk action limits). @default 500 */
    maxItems: PropTypes.number,
    /** Show selection counter in UI. @default true */
    showCounter: PropTypes.bool,
    /** Callback when selection limit reached. Signature: (maxItems, attemptedCount, excess) => void */
    onLimitReached: PropTypes.func,
});

/**
 * Preferences configuration for user preferences persistence.
 * All properties are optional.
 */
const preferencesConfigShape = PropTypes.shape({
    /** Disable user preferences system. @default false */
    disabled: PropTypes.bool,
    /** Don't persist tag preferences. @default false */
    noTags: PropTypes.bool,
    /** Don't read/persist the split preference (always starts unchecked). @default false */
    noSplit: PropTypes.bool,
});

/**
 * Defaults configuration for initial values.
 * All properties are optional with sensible defaults.
 */
const defaultsConfigShape = PropTypes.shape({
    /** Default sort configuration. @default { field: 'id', order: 'ASC' } */
    sort: PropTypes.shape({
        /** Field to sort by. */
        field: PropTypes.string.isRequired,
        /** Sort direction. */
        order: PropTypes.oneOf(['ASC', 'DESC']).isRequired,
    }),
    /** Default rows per page. @default 10 */
    rowsPerPage: PropTypes.number,
    /** Available page size options. */
    valuesPerPage: PropTypes.arrayOf(PropTypes.number),
});

/**
 * Messages configuration for custom i18n strings.
 * All properties are optional - defaults to intl messages.
 */
const messagesConfigShape = PropTypes.shape({
    /** Message shown when no more items to load. */
    noMoreItemsMessage: PropTypes.string,
    /** Message shown when no data. */
    emptyMessage: PropTypes.string,
});

/**
 * Empty state configuration for customizing empty UI.
 * All properties are optional - auto-detected by default.
 */
const emptyConfigShape = PropTypes.shape({
    /** Override automatic empty type detection. */
    type: PropTypes.oneOf(['initial', 'no-data', 'filtered-empty', 'search-empty', 'error']),
    /** Custom description text. */
    description: PropTypes.string,
    /** Custom image/icon component. */
    image: PropTypes.node,
    /** Action button text. */
    actionText: PropTypes.string,
    /** Action button click handler. */
    onAction: PropTypes.func,
});

/**
 * Virtualization configuration for table performance tuning.
 * All properties are optional with performance-optimized defaults.
 */
const virtualizationConfigShape = PropTypes.shape({
    /** Number of skeleton rows during load-more. @default 5 */
    skeletonCount: PropTypes.number,
    /** Default estimated row height (pixels). @default 53 */
    rowHeight: PropTypes.number,
    /** Base row height for density calculations. @default 21 */
    baseRowHeight: PropTypes.number,
    /** Row height adjustment per density level. @default 7 */
    condensationHeight: PropTypes.number,
    /** Multiplier for density height calculation. @default 2 */
    heightMultiplier: PropTypes.number,
    /** Buffer rows above/below viewport. @default 15 */
    overscan: PropTypes.number,
    /** Rows from end to trigger load-more. @default 5 */
    scrollThreshold: PropTypes.number,
    /** Base pixel offset for locked columns. @default 43 */
    lockedColumnOffset: PropTypes.number,
});

/**
 * Column definition shape.
 * Each column MUST have a 'field' property.
 */
const columnShape = PropTypes.shape({
    /** Field name/path in data object. REQUIRED. */
    field: PropTypes.string.isRequired,
    /** Column header text. Defaults to field name if not provided. */
    header: PropTypes.string,
    /** Show column initially. @default true */
    show: PropTypes.bool,
    /** Enable sorting for this column. @default true */
    sortable: PropTypes.bool,
    /** Column width (pixels or CSS string). */
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Minimum column width (pixels). */
    minWidth: PropTypes.number,
    /** Maximum column width (pixels). */
    maxWidth: PropTypes.number,
    /** Lock column to left side (always visible). @default false */
    locked: PropTypes.bool,
    /** Custom cell render function. Signature: (value, row, column) => ReactNode */
    render: PropTypes.func,
    /** Date/time format for export. */
    format: PropTypes.oneOf(['date', 'datetime', 'time', 'timezone']),
});

/**
 * Custom validator: Requires either graphqlConfig or externalDataConfig
 */
const requireDataSource = (props, propName, componentName) => {
    if (!props.graphqlConfig && !props.externalDataConfig) {
        return new Error(
            `${componentName}: Either 'graphqlConfig' or 'externalDataConfig' is required. ` +
            `Provide 'graphqlConfig' for Common Redux mode, or 'externalDataConfig' for parent-managed data.`
        );
    }
    return null;
};

/**
 * Custom validator: Warns if search is enabled but autocomplete fields are missing
 */
const validateSearchConfig = (props, propName, componentName) => {
    const searchConfig = props.searchConfig;
    if (searchConfig && !searchConfig.disabled && !searchConfig.noOptions) {
        if (!searchConfig.optionValue || !searchConfig.optionLabel) {
            // Warning instead of error - allows backwards compatibility
            console.warn(
                `${componentName}: searchConfig is missing 'optionValue' and/or 'optionLabel'. ` +
                `This will cause 'undefined' fields in autocomplete GraphQL queries. ` +
                `Either provide these fields or set 'noOptions: true' to disable autocomplete. ` +
                `See: docs/BUG-ANALYSIS-undefined-query-fields.md`
            );
        }
    }
    return null;
};

DataView.propTypes = {
    // ═══════════════════════════════════════════════════════════════════════════
    // CORE PROPS (Required)
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Unique identifier for preferences storage.
     * Used to persist user column preferences, sort order, etc.
     * REQUIRED.
     */
    name: PropTypes.string.isRequired,
    
    /**
     * Column definitions array.
     * Each column must have at least a 'field' property.
     * REQUIRED.
     */
    columns: PropTypes.arrayOf(columnShape).isRequired,
    
    // ═══════════════════════════════════════════════════════════════════════════
    // CORE PROPS (Optional with defaults)
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Entity type identifier for Redux state namespacing.
     * Used by Common Redux mode to separate data between different lists.
     * @default "default-list"
     */
    entity: PropTypes.string,
    
    /**
     * Enable infinite scroll mode instead of traditional pagination.
     * @default true
     */
    infiniteScroll: PropTypes.bool,
    
    /**
     * Clear entity state when component unmounts.
     * Set to false to persist data when navigating away.
     * @default true
     */
    clearOnUnmount: PropTypes.bool,
    
    // ═══════════════════════════════════════════════════════════════════════════
    // DATA SOURCE CONFIGURATION (One Required)
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * GraphQL configuration for Common Redux data fetching.
     * REQUIRED when not using externalDataConfig.
     * 
     * Required properties within:
     * - function: GraphQL function name
     * - conditionsType: GraphQL conditions input type
     */
    graphqlConfig: (props, propName, componentName) => {
        // First run the shape validation
        const shapeError = graphqlConfigShape(props, propName, componentName);
        if (shapeError) return shapeError;
        // Then run custom data source validation
        return requireDataSource(props, propName, componentName);
    },
    
    /**
     * External data configuration for parent-managed data.
     * REQUIRED when not using graphqlConfig.
     * 
     * Required properties within:
     * - data: Array of items
     * - onFetchRequest: Fetch callback
     */
    externalDataConfig: (props, propName, componentName) => {
        // First run the shape validation
        const shapeError = externalDataConfigShape(props, propName, componentName);
        if (shapeError) return shapeError;
        // Data source validation already handled by graphqlConfig validator
        return null;
    },
    
    // ═══════════════════════════════════════════════════════════════════════════
    // GROUPED CONFIGURATION OBJECTS (Optional)
    // ═══════════════════════════════════════════════════════════════════════════
    
    /**
     * Search bar configuration.
     * 
     * IMPORTANT: When search is enabled (disabled !== true) and autocomplete
     * is used (noOptions !== true), you MUST provide:
     * - optionValue: Field for dropdown option value
     * - optionLabel: Field for dropdown option display text
     * 
     * Omitting these causes malformed GraphQL queries with 'undefined' fields.
     */
    searchConfig: (props, propName, componentName) => {
        const shapeError = searchConfigShape(props, propName, componentName);
        if (shapeError) return shapeError;
        return validateSearchConfig(props, propName, componentName);
    },
    
    /**
     * Export configuration for CSV/PDF downloads.
     * Optional - exports disabled if not provided.
     */
    exportConfig: exportConfigShape,
    
    /**
     * UI/Layout configuration for visibility toggles and sizing.
     * Optional - uses defaults if not provided.
     */
    uiConfig: uiConfigShape,
    
    /**
     * Row selection configuration.
     * Optional - uses defaults if not provided.
     */
    selectionConfig: selectionConfigShape,
    
    /**
     * User preferences configuration.
     * Optional - preferences enabled by default.
     */
    preferencesConfig: preferencesConfigShape,
    
    /**
     * Default values configuration (sort, pagination).
     * Optional - uses component defaults.
     */
    defaultsConfig: defaultsConfigShape,
    
    /**
     * Custom i18n messages configuration.
     * Optional - uses intl messages by default.
     */
    messagesConfig: messagesConfigShape,
    
    /**
     * Empty state UI configuration.
     * Optional - auto-detects empty type.
     */
    emptyConfig: emptyConfigShape,
    
    /**
     * Virtualization performance configuration.
     * Optional - uses performance-optimized defaults.
     */
    virtualizationConfig: virtualizationConfigShape,
    
    // ═══════════════════════════════════════════════════════════════════════════
    // EVENT CALLBACKS (Optional)
    // ═══════════════════════════════════════════════════════════════════════════
    
    /** Called when page changes (non-infinite scroll mode). */
    onChangePage: PropTypes.func,
    
    /** Called when rows per page changes. */
    onChangeRowsPerPage: PropTypes.func,
    
    /** Called when search value changes. */
    onChangeSearch: PropTypes.func,
    
    /** Called when conditions change (search, filters). */
    onChangeConditions: PropTypes.func,
    
    /** Called when columns change (reorder, resize, visibility). */
    onChangeColumns: PropTypes.func,
    
    /** Called when row selection changes. Signature: (selectedItems) => void */
    onSelectedItems: PropTypes.func,
    
    /** Called when filter button is clicked. */
    onFilters: PropTypes.func,
    
    /** Called for external sort handling. */
    onExternalSorting: PropTypes.func,
    
    /** Called when total count changes. */
    onChangeTotal: PropTypes.func,
    
    // ═══════════════════════════════════════════════════════════════════════════
    // TABLE CUSTOMIZATION (Optional)
    // ═══════════════════════════════════════════════════════════════════════════
    
    /** Table row options (actions menu, row click, etc.). */
    options: PropTypes.object,
    
    /**
     * Unique row identifier for selection and virtualization.
     * Can be a string field name OR a function for composite keys.
     * 
     * @example
     * // String field name (default)
     * keyColumn="id"
     * 
     * @example
     * // Function for composite keys (when IDs may not be unique)
     * keyColumn={(item, index) => `${item.id}-${index}`}
     * 
     * @default "id"
     */
    keyColumn: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    
    /** Custom table buttons configuration. */
    tableButtons: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    
    /** Context menu options array. */
    menuOptions: PropTypes.array,
    
    /** Menu click handler. Signature: (menuItem, row) => void */
    onClickMenu: PropTypes.func,
    
    /** Custom column renderer. */
    customColumn: PropTypes.func,
    
    /** Extra button to render in toolbar. */
    extraButton: PropTypes.node,
    
    /** Extra filters to render (e.g., search type selector). */
    extraFilters: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    
    /** Show error state. @default false */
    showError: PropTypes.bool,
    
    /**
     * Conditions that are ALWAYS merged into every getData() call.
     * Use for mandatory filters (e.g., companyId) that must never be dropped.
     * Unlike conditions set via onChangeConditions, these survive refresh, sort,
     * and scroll operations even when onChangeConditions hasn't fired.
     * 
     * @example
     * permanentConditions={[
     *   { fields: ["companyId"], terms: [companyId], exact: true, conditionOperator: "AND" }
     * ]}
     */
    permanentConditions: PropTypes.array,
    
    /** 
     * Whether to show total count badge and fetch total count from API.
     * When false, the total badge is hidden and no total count API requests are made.
     * Useful for external integrations that don't provide total count.
     * @default true 
     */
    showTotalCount: PropTypes.bool,
};

DataView.defaultProps = {
    entity: "default-list",
    infiniteScroll: true,
    clearOnUnmount: false,
};

export default DataView